import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { fetchCategoryEntries, fetchReviewSlugs } from "@/lib/libraryApi";
import { VIRTUE_JOURNEYS } from "@/lib/journeyNetwork";
import { breadcrumbJsonLd } from "@/components/LibraryBreadcrumb";

/**
 * The Virtues — the Compass Rose antechamber (Founder directive, 2026-07).
 *
 * A cinematic entrance, not a webpage: the approved artwork fills the
 * screen behind warm golden ambient light and drifting dust. The title
 * emerges, then the invitation — only then does the Compass Rose become
 * active. Ken Burns moves image AND buttons together (one stage), so the
 * HTML buttons stay perfectly aligned with the Guardian medallions.
 * Artwork is background only — every word on this page is HTML (EN/NL).
 */

// Medallion centres as % of the artwork. Mobile = virtues-compass-mobile.webp
// (924x2000), desktop (lg:) = virtues-compass-desktop.jpg (1170x781).
const COMPASS = [
  { slug: "wisdom",     greekKey: "library.journey.wisdom.greek",
    pos: "left-1/2 top-[23.5%] lg:left-1/2 lg:top-[19%]" },
  { slug: "courage",    greekKey: "library.journey.courage.greek",
    pos: "left-[83%] top-[49.5%] lg:left-[78.5%] lg:top-[51%]" },
  { slug: "justice",    greekKey: "library.journey.justice.greek",
    pos: "left-[17%] top-[49.5%] lg:left-[21.5%] lg:top-[51%]" },
  { slug: "temperance", greekKey: "library.journey.temperance.greek",
    pos: "left-1/2 top-[75%] lg:left-1/2 lg:top-[84%]" },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function VirtuesCompass() {
  const { t } = useT();
  const [published, setPublished] = useState([]);
  const [inReview, setInReview] = useState([]);
  const [active, setActive] = useState(prefersReducedMotion);

  useEffect(() => {
    let alive = true;
    fetchCategoryEntries("virtues")
      .then((data) => { if (alive) setPublished((data.entries || []).map((e) => e.slug)); })
      .catch(() => {});
    // Founder preview: with an admin token, Journeys in REVIEW open from the
    // Compass exactly as a future member would experience them. Public
    // visitors have no token — nothing changes for them.
    let adminToken = "";
    try { adminToken = localStorage.getItem("stoicos.admin.token") || ""; } catch { /* ignore */ }
    if (adminToken) {
      fetchReviewSlugs("virtues", adminToken)
        .then((slugs) => { if (alive) setInReview(slugs); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, []);

  // The compass awakens only after the title and invitation have spoken.
  useEffect(() => {
    if (active) return;
    const timer = setTimeout(() => setActive(true), 3800);
    return () => clearTimeout(timer);
  }, [active]);

  useSEO({
    title: `${t("library.virtues.heroTitle")} | ${t("library.hero.title")} | StoicOs.ai`,
    description: t("library.cats.virtues.desc"),
    canonical: "https://stoicos.ai/library/virtues",
    og: { image: "https://stoicos.ai/og-library.jpg" },
    jsonLd: [
      breadcrumbJsonLd([
        { label: t("academy.nav"), href: "https://stoicos.ai/academy" },
        { label: t("library.hero.title"), href: "https://stoicos.ai/library" },
        { label: t("library.virtues.heroTitle"), href: "https://stoicos.ai/library/virtues" },
      ]),
    ],
  });

  return (
    <main data-testid="virtues-compass-page" className="bg-black text-bone">
      <section className="relative h-[100svh] overflow-hidden bg-black flex items-center justify-center">

        {/* The chamber — the artwork itself, blurred, fills every pixel.
            There is no card and no edge: one continuous scene. */}
        <div
          aria-hidden
          className="vc-backdrop absolute inset-0 hidden lg:block"
          style={{ backgroundImage: "url(/virtues-compass-desktop.jpg)" }}
        />
        <div
          aria-hidden
          className="vc-backdrop absolute inset-0 lg:hidden"
          style={{ backgroundImage: "url(/virtues-compass-mobile.webp)" }}
        />

        {/* The stage — image + buttons share one Ken Burns transform,
            so alignment with the medallions is always preserved. */}
        <div className="vc-fadein relative">
          <div className="vc-kenburns relative">
            <picture>
              <source media="(min-width: 1024px)" srcSet="/virtues-compass-desktop.jpg" />
              <img
                src="/virtues-compass-mobile.webp"
                alt={t("library.cats.virtues.desc")}
                data-testid="virtues-compass-image"
                className="vc-img-mask block w-auto h-auto max-h-[100svh] max-w-[100vw] lg:max-h-[78svh]"
              />
            </picture>

            {VIRTUE_JOURNEYS.map(({ number, slug }) => {
              const { greekKey, pos } = COMPASS.find((c) => c.slug === slug);
              const isPublished = published.includes(slug);
              const isFounderPreview = !isPublished && inReview.includes(slug);
              const isOpen = isPublished || isFounderPreview;
              const inner = (
                <>
                  <span
                    aria-hidden
                    className="block rounded-full w-full h-full
                      border border-transparent transition-[border-color,box-shadow] duration-300
                      group-hover:border-gold/50 group-hover:shadow-[0_0_50px_-10px_rgba(232,200,121,0.35)]"
                  />
                  <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 text-center whitespace-nowrap">
                    <span className={`block text-[11px] lg:text-xs tracking-[0.3em] uppercase ${isOpen ? "text-gold" : "text-gold/45"}`}>
                      {t(`library.journey.${slug}.name`)}
                    </span>
                    <span className={`block mt-0.5 font-serif italic text-[10px] lg:text-[11px] ${isOpen ? "text-bone/70" : "text-bone/35"}`}>
                      {t(greekKey)}
                    </span>
                  </span>
                </>
              );
              const cls = `absolute -translate-x-1/2 -translate-y-1/2 group w-[32.5%] lg:w-[16.2%] aspect-square ${pos}
                transition-opacity duration-1000 ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`;
              return isOpen ? (
                <Link
                  key={slug}
                  to={`/library/${slug}`}
                  data-testid={`virtues-compass-${slug}`}
                  aria-label={`Journey ${number}: ${t(`library.journey.${slug}.name`)}`}
                  className={cls}
                >
                  {inner}
                </Link>
              ) : (
                <div
                  key={slug}
                  data-testid={`virtues-compass-${slug}`}
                  aria-label={t("library.journey.soon")}
                  title={t("library.journey.soon")}
                  className={`${cls} cursor-default`}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>

        {/* Warm golden ambient light + deep vignette */}
        <div className="vc-ambient absolute inset-0 pointer-events-none" aria-hidden />

        {/* Dust drifting in the light */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <span className="vc-dust" style={{ left: "44%", top: "30%", animationDuration: "11s" }} />
          <span className="vc-dust" style={{ left: "50%", top: "22%", animationDuration: "14s", animationDelay: "1.2s" }} />
          <span className="vc-dust" style={{ left: "56%", top: "34%", animationDuration: "10s", animationDelay: "2.6s" }} />
          <span className="vc-dust" style={{ left: "47%", top: "48%", animationDuration: "16s", animationDelay: "0.6s" }} />
          <span className="vc-dust" style={{ left: "53%", top: "58%", animationDuration: "13s", animationDelay: "3.4s" }} />
          <span className="vc-dust" style={{ left: "40%", top: "40%", animationDuration: "15s", animationDelay: "1.8s" }} />
          <span className="vc-dust" style={{ left: "60%", top: "26%", animationDuration: "12s", animationDelay: "4.2s" }} />
        </div>

        {/* The antechamber — veil, the words emerge, then withdraw */}
        <div className="vc-veil absolute inset-0 pointer-events-none" aria-hidden />
        <div aria-hidden className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none">
          <div
            data-testid="virtues-intro-title"
            className="vc-title font-serif text-4xl sm:text-5xl lg:text-6xl tracking-[0.18em] uppercase text-bone"
          >
            {t("library.virtues.heroTitle")}
          </div>
          <p className="vc-quote mt-6 font-serif italic text-base md:text-lg text-gold/90 max-w-xl leading-relaxed">
            {t("library.virtues.heroLine")}
          </p>
        </div>

        {/* The title settles — floating above the artwork, part of the scene */}
        <div
          className={`absolute top-20 lg:top-16 inset-x-0 text-center px-6 pointer-events-none transition-opacity duration-[1500ms] ${active ? "opacity-100" : "opacity-0"}`}
          style={{ textShadow: "0 2px 26px rgba(0,0,0,0.85)" }}
        >
          <h1
            data-testid="virtues-hero-title"
            className="font-serif text-xl md:text-2xl tracking-[0.32em] uppercase text-bone"
          >
            {t("library.virtues.heroTitle")}
          </h1>
          <p
            data-testid="virtues-hero-line"
            className="mt-1.5 font-serif italic text-xs md:text-sm text-gold/85"
          >
            {t("library.virtues.heroLine")}
          </p>
        </div>

        {/* The invitation — and the way back */}
        <div
          className={`absolute inset-x-0 bottom-6 md:bottom-9 text-center transition-opacity duration-[1500ms] ${active ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <p
            data-testid="virtues-compass-invite"
            className="text-[11px] md:text-xs tracking-[0.32em] uppercase text-gold/90"
          >
            {t("library.virtues.choose")}
          </p>
          <Link
            to="/library"
            data-testid="virtues-compass-back"
            className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/50 hover:text-gold transition-colors"
          >
            ← {t("library.journey.return")}
          </Link>
        </div>
      </section>
    </main>
  );
}
